import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Minus } from 'lucide-react';
import { formatTelefoneBR } from '@fiodecobre/shared';
import { useAuth } from '../lib/auth';
import { Avatar } from './Avatar';
import { atendimentoApi } from '../modules/atendimento/lib/api';
import { useAlertaFaixa } from './alertas/AlertasContexto';

interface Esquecido {
  id: number;
  nome: string | null;
  telefone: string | null;
  jid: string;
  foto_url: string | null;
  atendente_nome: string | null;
  congelada_em: string;
  horas: number;
}

const POLLING_MS = 5 * 60 * 1000;
// Amarelo-ouro solido + texto preto: distinto do PARADO (laranja) e do PERDIDO (ambar escuro),
// daltonismo-safe (feedback_alertas_fundo_solido). Acento so na borda.
const FUNDO = '#eab308';
const BORDA = '#a16207';
const TEXTO = '#000';

function identContato(p: Esquecido): string {
  const fone = p.telefone ? formatTelefoneBR(p.telefone) : '';
  if (p.nome) return fone ? `${p.nome} · ${fone}` : p.nome;
  return fone || p.jid;
}

function haQuanto(p: Esquecido): string {
  if (p.horas < 24) return `ha ${p.horas}h`;
  const d = Math.floor(p.horas / 24);
  return `ha ${d}d`;
}

// Chamados CONGELADOS (RDS 1.18) esquecidos por +16h sem retorno (RDS 1.19/Fase 3): cada um ja
// virou advertencia de ponto pra quem congelou (conta pro bonus semanal — DA-146). So admin/gerente.
// A faixa some sozinha quando o chamado e resolvido (encerrar/transferir/cliente reescrever).
// Clicar abre a conversa no Atendimento pra resolver.
export function AlertaChamadosEsquecidosBanner() {
  const navigate = useNavigate();
  const { usuario } = useAuth();
  const [itens, setItens] = useState<Esquecido[]>([]);
  const [aberto, setAberto] = useState(false);

  const podeVer = usuario && ['admin', 'gerente'].includes(usuario.perfil);

  useEffect(() => {
    if (!podeVer) return;
    let cancelado = false;
    async function tick() {
      try {
        const r = await atendimentoApi.listarChamadosEsquecidos();
        if (!cancelado) setItens(r.itens);
      } catch { /* silencia */ }
    }
    void tick();
    const id = setInterval(tick, POLLING_MS);
    return () => { cancelado = true; clearInterval(id); };
  }, [podeVer]);

  const ativo = !!podeVer && itens.length > 0;
  const recolhido = useAlertaFaixa(
    'esquecidos',
    ativo ? { rotulo: 'ESQUECIDO', bg: FUNDO, borda: BORDA, qtd: itens.length } : null,
  );

  if (!ativo || recolhido) return null;

  function abrirConversa(id: number) {
    // Deep-link: o AtendimentoHome restaura a conversa ativa do localStorage.
    localStorage.setItem('fiodecobre_conversa_ativa', String(id));
    navigate('/atendimento');
  }

  const maxHoras = itens.reduce((m, p) => Math.max(m, p.horas), 0);

  return (
    <div style={{ background: FUNDO, borderLeft: `4px solid ${BORDA}`, color: TEXTO, fontSize: 13 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px' }}>
        <button
          type="button"
          onClick={() => setAberto((o) => !o)}
          title={aberto ? 'Recolher' : 'Ver chamados esquecidos'}
          style={{
            background: 'rgba(0,0,0,0.18)', color: TEXTO, border: 'none', borderRadius: 6, cursor: 'pointer',
            width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}
        >
          {aberto ? <Minus size={15} /> : <Plus size={15} />}
        </button>
        <span style={{ fontWeight: 700, letterSpacing: 0.3 }}>ESQUECIDO</span>
        <span>
          {itens.length} chamado{itens.length === 1 ? '' : 's'} congelado{itens.length === 1 ? '' : 's'} ha +16h sem retorno ({haQuanto({ horas: maxHoras } as Esquecido)}) — virou advertencia no ponto. Resolva.
        </span>
      </div>

      {aberto && (
        <div style={{ padding: '0 14px 10px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {/* <div role=button>, nao <button>: texto dentro de <button> nao se seleciona,
              e o telefone precisa ser copiavel com o mouse. Com selecao ativa, nao navega. */}
          {itens.map((p) => (
            <div
              key={p.id}
              role="button"
              tabIndex={0}
              onClick={() => { if (!window.getSelection()?.toString()) abrirConversa(p.id); }}
              onKeyDown={(e) => { if (e.key === 'Enter') abrirConversa(p.id); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left',
                background: 'rgba(255,255,255,0.80)', border: `1px solid rgba(0,0,0,0.15)`,
                borderRadius: 6, padding: '6px 8px', cursor: 'pointer', color: '#000',
              }}
            >
              <Avatar nome={p.nome || p.telefone || '?'} fotoUrl={p.foto_url} tamanho={26} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', userSelect: 'text', cursor: 'text' }}>
                  {identContato(p)}
                </div>
                <div style={{ fontSize: 11 }}>
                  Congelou: <b>{p.atendente_nome ? p.atendente_nome.replace(/\s+FIODECOBRE$/i, '') : '—'}</b> · sem retorno {haQuanto(p)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
